import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

type QuantityInputProps = {
  productName: string | null;
  value: number | undefined;
  onChange: (quantity: number) => void;
};

export function QuantityInput({ productName, value: valueProp, onChange }: QuantityInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(valueProp?.toString() || '0');

  useEffect(() => {
    // Update input value when quantity prop changes
    setInputValue(valueProp?.toString() || '0');
  }, [valueProp]);

  const handleDecrease = () => {
    if (!!valueProp && valueProp > 0) {
      onChange(valueProp - 1);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // On blur, ensure the input value is a valid number
    if (inputValue === '') {
      onChange(0);
      return;
    }
  };

  const handleIncrease = () => {
    onChange((valueProp || 0) + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // If empty, call onChange with 0
    if (newValue === '') {
      return;
    }

    const numValue = parseFloat(newValue);
    const validValue = Math.max(0, numValue); // Ensure non-negative

    onChange(validValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <div className="flex flex-row items-center rounded-sm border border-solid border-gray-300 bg-white p-1 text-clip focus-within:border-procuros-green-400">
      <button
        type="button"
        onClick={handleDecrease}
        aria-label={t('orderForms:form.product.decreaseQuantity')}
        className="inline-flex shrink-0 items-center rounded-md px-[11px] py-[9px] text-clip hover:bg-procuros-green-50 [&:hover>*]:text-procuros-green-500"
      >
        <MinusIcon className="size-4 text-gray-600" />
      </button>
      <input
        name={`${productName}-quantity`}
        type="number"
        min={0}
        step={0.01}
        aria-label={t('orderForms:form.product.quantity', { productName })}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        className="w-[50px] border-none bg-transparent px-3 py-1 text-center text-sm font-normal text-gray-700 outline-hidden focus:ring-0"
      />
      <button
        type="button"
        onClick={handleIncrease}
        aria-label={t('orderForms:form.product.increaseQuantity')}
        className="inline-flex shrink-0 items-center rounded-md px-[11px] py-[9px] text-clip hover:bg-procuros-green-50 [&:hover>*]:text-procuros-green-500"
      >
        <PlusIcon className="size-4 text-gray-600" />
      </button>
    </div>
  );
}
